import { z } from 'zod';
import { getRequest, patchRequest, postRequest, deleteRequest } from './amplify/helpers';
import { CommonEntitySchema } from './entity-common/types';
import { UserUploadedFileSchema } from './upload-temp-file';
import { MediaAssetType } from '@/types/media';
import { AvatarType } from '@/types/avatar';

export enum PersonalAvatarInputType {
  SINGLE_IMAGE = 'SINGLE_IMAGE',
}

export const AvatarVoiceInputSchema = z.union([
  z.object({
    isNew: z.boolean().optional(),
    type: z.literal('existing'),
    elevenLabsVoiceId: z.string(),
    languageCode: z.string().optional(),
  }),
  z.object({
    isNew: z.boolean().optional(),
    type: z.literal('uploaded'),
    file: UserUploadedFileSchema,
  }),
]);
export type AvatarVoiceInput = z.infer<typeof AvatarVoiceInputSchema>;

export const SingleImageAvatarInputDataSchema = z.object({
  avatarImageFile: z.object({
    isNew: z.boolean().optional(),
    src: z.string(),
    type: z.nativeEnum(MediaAssetType),
  }),
  avatarVoiceInput: AvatarVoiceInputSchema,
});
export const SingleImageAvatarInputSchema = SingleImageAvatarInputDataSchema.extend({
  type: z.literal(PersonalAvatarInputType.SINGLE_IMAGE),
});
export type SingleImageAvatarInputData = z.infer<typeof SingleImageAvatarInputDataSchema>;
export type SingleImageAvatarInput = z.infer<typeof SingleImageAvatarInputSchema>;

// To be extended with z.union([SingleImageAvatarInputSchema, ...])
export const PersonalAvatarInputSchema = SingleImageAvatarInputSchema;
export type PersonalAvatarInput = z.infer<typeof PersonalAvatarInputSchema>;

export enum PersonalAvatarVoiceProvider {
  ELEVEN_LABS_CLONE = 'ELEVEN_LABS_CLONE',
  SIEVE_CLONE = 'SIEVE_CLONE',
}

export enum PersonalAvatarStatus {
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

export enum PersonalAvatarVersion {
  SINGLE_IMAGE_1_0 = 'SINGLE_IMAGE_1_0',
}

export const PersonalAvatarVoiceSchema = z.object({
  provider: z.nativeEnum(PersonalAvatarVoiceProvider),
  voiceId: z.string().optional(),
  voiceName: z.string(),
  voiceTrainingSourceS3Key: z.string(),
  languageCode: z.string().optional(),
});
export type PersonalAvatarVoice = z.infer<typeof PersonalAvatarVoiceSchema>;

export const AvatarFitSettingsSchema = z.union([
  z.object({ type: z.enum(['contain', 'cover']) }),
  z.object({
    type: z.literal('crop'),
    crop: z.object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    }),
  }),
]);

export const PersonalAvatarImageSchema = z.object({
  imageS3Key: z.string(),
  posterS3key: z.string(),
  width: z.number(),
  height: z.number(),
  fullscreenFit: AvatarFitSettingsSchema,
});
export type PersonalAvatarImage = z.infer<typeof PersonalAvatarImageSchema>;

export const PersonalAvatarIdentifierSchema = z.object({
  appKey: z.string(),
  id: z.string(),
});
export type PersonalAvatarIdentifier = z.infer<typeof PersonalAvatarIdentifierSchema>;

export const PersonalAvatarSchema = CommonEntitySchema.extend(PersonalAvatarIdentifierSchema.shape).extend({
  name: z.string(),
  version: z.nativeEnum(PersonalAvatarVersion),
  status: z.nativeEnum(PersonalAvatarStatus),
  avatarImage: z.optional(PersonalAvatarImageSchema),
  trainedVoice: z.optional(PersonalAvatarVoiceSchema),
  description: z.string().optional(),
  tags: z.array(z.string()),
  inputParams: PersonalAvatarInputSchema,
});

export type PersonalAvatarData = z.infer<typeof PersonalAvatarSchema>;

export type PersonalAvatarViewData = Omit<PersonalAvatarData, 'inputParams'> & {
  avatarImage?: PersonalAvatarImage & {
    imageUrl: string;
    posterUrl: string;
  };
  trainedVoice?: PersonalAvatarVoice & {
    voiceTrainingSourceUrl?: string;
  };
  type: AvatarType;
};

export const CreatePersonalAvatarSchema = z.object({
  appKey: z.string(),
  name: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  inputParams: SingleImageAvatarInputDataSchema,
});
export type CreatePersonalAvatarRequest = z.infer<typeof CreatePersonalAvatarSchema>;
export const createPersonalAvatar = async (request: CreatePersonalAvatarRequest): Promise<PersonalAvatarViewData> =>
  postRequest({
    apiName: 'studio',
    path: '/personal-avatar/create',
    options: { body: request },
  });

const UpdatePersonalAvatarByIdSchema = z.object({
  appKey: z.string(),
  id: z.string(),
  name: z.string(),
});

export const updatePersonalAvatarById = async (
  body: z.infer<typeof UpdatePersonalAvatarByIdSchema>
): Promise<{ personalAvatar: PersonalAvatarViewData }> =>
  patchRequest({
    apiName: 'studio',
    path: '/personal-avatar/update',
    options: { body },
  });

const DeletePersonalAvatarByIdSchema = z.object({
  appKey: z.string(),
  id: z.string(),
});

export const deletePersonalAvatarById = async (
  request: z.infer<typeof DeletePersonalAvatarByIdSchema>
): Promise<{ personalAvatar: PersonalAvatarViewData }> =>
  deleteRequest({
    apiName: 'studio',
    path: '/personal-avatar/delete',
    options: { queryParams: request },
  });

export const ListPersonalAvatarsSchema = z.object({
  appKey: z.string(),
});
export const listPersonalAvatars = async (
  request: z.infer<typeof ListPersonalAvatarsSchema>
): Promise<PersonalAvatarViewData[]> => {
  const data = await postRequest({
    apiName: 'studio',
    path: '/personal-avatar/list',
    options: { body: request },
  });
  return data?.avatars;
};

export const GetPersonalAvatarSchema = z.object({
  appKey: z.string(),
  id: z.string(),
});
export const getPersonalAvatar = async (
  request: z.infer<typeof GetPersonalAvatarSchema>
): Promise<PersonalAvatarViewData> => {
  return await postRequest({
    apiName: 'studio',
    path: '/personal-avatar/get',
    options: { body: request },
  });
};

export const GetPersonalAvatarsByIdsSchema = z.object({
  appKey: z.string(),
  ids: z.array(z.string()),
});
export const getPersonalAvatarsByIds = async (
  request: z.infer<typeof GetPersonalAvatarsByIdsSchema>
): Promise<PersonalAvatarViewData[]> => {
  const data = await postRequest({
    apiName: 'studio',
    path: '/personal-avatar/get-by-ids',
    options: { body: request },
  });
  return data?.avatars;
};

export type PersonalAvatarVoiceView = {
  provider: PersonalAvatarVoiceProvider;
  voiceId: string;
  name: string;
  gender: 'female' | 'male';
  accent?: string;
  description?: string;
  previewUrl: string;
  labels: Record<string, string>;
};
export const GetPersonalAvatarVoicesSchema = z.object({
  appKey: z.string(),
});
export const getPersonalAvatarVoices = async (
  request: z.infer<typeof GetPersonalAvatarVoicesSchema>
): Promise<PersonalAvatarVoiceView[]> => {
  return await getRequest({
    apiName: 'studio',
    path: '/personal-avatar/get-voices',
    options: { queryParams: request },
  });
};
